import * as React from 'react';

type PossibleRef<T> = React.Ref<T> | undefined;

type RefCacheNode = WeakMap<object, RefCacheNode | React.RefCallback<unknown>>;

const cacheTerminalKey = {};
const undefinedRefKey = {};
const composedRefCache: RefCacheNode = new WeakMap();

function setRef<T>(ref: PossibleRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  if (ref) {
    ref.current = value;
  }
}

function refCacheKey<T>(ref: PossibleRef<T>): object {
  return (ref ?? undefinedRefKey) as object;
}

type ComposedRefController<T> = {
  lastNode: T | null | undefined;
  depth: number;
  detachGeneration: number;
};

function createComposedRefController<T>(): ComposedRefController<T> {
  return { lastNode: undefined, depth: 0, detachGeneration: 0 };
}

function commitComposedNode<T>(
  controller: ComposedRefController<T>,
  refs: readonly PossibleRef<T>[],
  node: T | null,
) {
  if (controller.lastNode === node) return;

  controller.depth += 1;
  controller.lastNode = node;
  try {
    for (const ref of refs) {
      setRef(ref, node);
    }
  } finally {
    controller.depth -= 1;
  }
}

/**
 * React 19 detaches a ref (calls it with null) before reattaching when the
 * callback identity changes. Radix passes setState as a ref, so that null
 * update re-renders, the ref identity changes again, and the input mounts
 * forever.
 *
 * A real unmount uses the same shape: ref(null) runs before removeChild, so
 * the node is still connected when null arrives. Wait until after this commit.
 * If the node is still connected, the null was a detach-before-reattach and
 * must be dropped. If it was removed, forward the unmount.
 */
function dispatchComposedNode<T>(
  controller: ComposedRefController<T>,
  getRefs: () => readonly PossibleRef<T>[],
  node: T | null,
) {
  if (controller.depth > 0) return;

  const previous = controller.lastNode;
  if (node == null && previous instanceof Node && previous.isConnected) {
    const generation = ++controller.detachGeneration;
    const detached = previous;
    queueMicrotask(() => {
      if (controller.detachGeneration !== generation) return;
      if (controller.lastNode !== detached) return;
      if (detached.isConnected) return;
      commitComposedNode(controller, getRefs(), null);
    });
    return;
  }

  controller.detachGeneration += 1;
  commitComposedNode(controller, getRefs(), node);
}

function createComposedRefCallback<T>(refs: PossibleRef<T>[]): React.RefCallback<T> {
  const controller = createComposedRefController<T>();
  return (node) => {
    dispatchComposedNode(controller, () => refs, node);
  };
}

export function composeRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  let cacheNode = composedRefCache;

  for (const ref of refs) {
    const key = refCacheKey(ref);
    const cached = cacheNode.get(key);
    if (cached instanceof WeakMap) {
      cacheNode = cached;
      continue;
    }

    const nextNode: RefCacheNode = new WeakMap();
    cacheNode.set(key, nextNode);
    cacheNode = nextNode;
  }

  const cachedCallback = cacheNode.get(cacheTerminalKey);
  if (typeof cachedCallback === 'function') {
    return cachedCallback as React.RefCallback<T>;
  }

  const callback = createComposedRefCallback(refs);
  cacheNode.set(cacheTerminalKey, callback as React.RefCallback<unknown>);
  return callback;
}

export function useComposedRefs<T>(...refs: PossibleRef<T>[]): React.RefCallback<T> {
  const refsRef = React.useRef(refs);
  refsRef.current = refs;
  const controllerRef = React.useRef<ComposedRefController<T> | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = createComposedRefController<T>();
  }

  // Stable identity. React 19 only detaches and reattaches a ref when this
  // function changes, and that detach calls setState and loops.
  return React.useCallback((node) => {
    const controller = controllerRef.current;
    if (controller === null) return;
    dispatchComposedNode(controller, () => refsRef.current, node);
  }, []);
}
